import * as THREE from "three";
import { createLaserExplosion } from "../effects/LaserExplosion";
import { handleProjectileHit } from "../damage";
import { Tank } from "../Tank";

// Sound effect for laser - to be implemented if audio system exists
// import { playSound } from "../audio/SoundSystem"; // Uncomment if sound system exists

const PROJECTILE_CONFIG = {
  SPEED: 4.0, // Szybszy niż błyskawica
  RANGE: 20.0, // Większy zasięg
  LASER_COLOR: 0xff3030, // Intensywny czerwony kolor dla lasera
  LASER_INTENSITY: 4, // Jaśniejsze światło
  LASER_RANGE: 18, // Większy zasięg światła
  LASER_BEAM_WIDTH: 0.08, // Szerokość promienia lasera
  LASER_TRAIL_COUNT: 6, // Liczba cząsteczek w śladzie
  LASER_TRAIL_LIFETIME: 0.8, // Czas życia śladu (w sekundach)
};

/**
 * Tworzy i animuje pocisk laserowy wystrzelony z podanej pozycji w podanym kierunku
 * @param scene Scena Three.js
 * @param startPosition Pozycja początkowa pocisku
 * @param direction Kierunek lotu pocisku
 * @param sourcePlayerId ID gracza, który wystrzelił pocisk
 */
export function fireProjectileLaser(
  scene: THREE.Scene,
  startPosition: THREE.Vector3,
  direction: THREE.Vector3,
  sourcePlayerId?: string,
) {
  // Tworzenie pocisku - kula energii laserowej
  // Reduced segments for performance (8,8 instead of 16,16)
  const projectileGeometry = new THREE.SphereGeometry(0.25, 8, 8); // Mniejszy ale bardziej szczegółowy
  const projectileMaterial = new THREE.MeshPhongMaterial({
    color: PROJECTILE_CONFIG.LASER_COLOR,
    emissive: PROJECTILE_CONFIG.LASER_COLOR,
    emissiveIntensity: 1.2,
    shininess: 150,
    transparent: true,
    opacity: 0.9,
  });
  const projectile = new THREE.Mesh(projectileGeometry, projectileMaterial);

  // Ustawienie pozycji początkowej
  projectile.position.copy(startPosition);

  // Dodanie pocisku do sceny
  scene.add(projectile);

  // Parametry pocisku
  const projectileSpeed = PROJECTILE_CONFIG.SPEED;
  const gravity = -0.01; // Mniejsza grawitacja dla lasera - bardziej "prostoliniowy" lot
  let time = 0;

  // Dodaj efekt świetlny do pocisku - jaśniejszy i większy zasięg
  const projectileLight = new THREE.PointLight(
    PROJECTILE_CONFIG.LASER_COLOR,
    PROJECTILE_CONFIG.LASER_INTENSITY,
    PROJECTILE_CONFIG.LASER_RANGE,
  );
  projectileLight.position.copy(projectile.position);
  scene.add(projectileLight);

  // Dodaj efekt poświaty (glow)
  // Reduced segments for performance (8,8 instead of 16,16)
  const glowGeometry = new THREE.SphereGeometry(0.4, 8, 8);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: PROJECTILE_CONFIG.LASER_COLOR,
    transparent: true,
    opacity: 0.4,
    side: THREE.BackSide,
  });
  const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
  glowMesh.position.copy(projectile.position);
  scene.add(glowMesh);

  // Odtwórz dźwięk lasera przy wystrzeleniu
  // if (typeof playSound === 'function') {
  //   playSound('laser', 0.7);
  // }

  // Funkcja tworząca efekt śladu laserowego za pociskiem
  const createLaserTrail = () => {
    // Tworzymy małe cząsteczki energii laserowej
    const particleGeometry = new THREE.SphereGeometry(0.06, 8, 8);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(
        1.0, // Pełny czerwony
        0.3 + Math.random() * 0.3, // Trochę zielonego dla efektu pomarańczowego
        0.1 + Math.random() * 0.2, // Minimalnie niebieskiego
      ),
      transparent: true,
      opacity: 0.8,
    });
    const particle = new THREE.Mesh(particleGeometry, particleMaterial);

    // Losowa pozycja wokół pocisku (bliżej niż w przypadku błyskawicy)
    const offset = new THREE.Vector3(
      (Math.random() - 0.5) * 0.2,
      (Math.random() - 0.5) * 0.2,
      (Math.random() - 0.5) * 0.2,
    );
    particle.position.copy(projectile.position).add(offset);
    scene.add(particle);

    // Animacja cząsteczki
    let particleTime = 0;
    const animateParticle = () => {
      particleTime += 0.016;

      // Pulsowanie cząsteczki
      const pulseScale = 1 + Math.sin(particleTime * 30) * 0.2;
      particle.scale.set(pulseScale, pulseScale, pulseScale);

      // Zanikanie
      particle.material.opacity = Math.max(0, 0.8 - particleTime * (1 / PROJECTILE_CONFIG.LASER_TRAIL_LIFETIME));

      if (particleTime > PROJECTILE_CONFIG.LASER_TRAIL_LIFETIME) {
        scene.remove(particle);
        return;
      }

      requestAnimationFrame(animateParticle);
    };

    animateParticle();
  };

  // Funkcja tworząca efekt promienia lasera
  const createLaserBeam = (start: THREE.Vector3, end: THREE.Vector3) => {
    // Tworzymy geometrię dla promienia lasera
    const beamGeometry = new THREE.CylinderGeometry(
      PROJECTILE_CONFIG.LASER_BEAM_WIDTH * 0.5, // Górna średnica
      PROJECTILE_CONFIG.LASER_BEAM_WIDTH, // Dolna średnica
      start.distanceTo(end), // Długość
      8, // Segmenty obwodu
      1, // Segmenty wysokości
      true, // Otwarty cylinder
    );

    // Obracamy geometrię, aby cylinder był zorientowany wzdłuż osi Y
    beamGeometry.rotateX(Math.PI / 2);

    // Materiał dla promienia lasera
    const beamMaterial = new THREE.MeshBasicMaterial({
      color: PROJECTILE_CONFIG.LASER_COLOR,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });

    const beam = new THREE.Mesh(beamGeometry, beamMaterial);

    // Ustawiamy pozycję i orientację promienia
    const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    beam.position.copy(midPoint);

    // Obracamy promień, aby wskazywał w kierunku końca
    beam.lookAt(end);

    // Dodajemy promień do sceny
    scene.add(beam);

    // Animacja zanikania promienia
    setTimeout(() => {
      scene.remove(beam);
      beamGeometry.dispose();
      beamMaterial.dispose();
    }, 100);
  };

  // Zapisz początkowy kierunek jako wektor prędkości
  const velocity = direction.clone().multiplyScalar(projectileSpeed);

  // Animacja lotu pocisku
  const animateProjectile = () => {
    time += 0.016; // ~60fps

    // Zastosuj grawitację do składowej Y prędkości, ale mniejszą dla lasera
    velocity.y += gravity; // Mniejsza grawitacja dla efektu "prostoliniowego" pocisku

    // Aktualizacja pozycji na podstawie prędkości
    projectile.position.add(velocity);

    // Aktualizacja pozycji efektu poświaty
    glowMesh.position.copy(projectile.position);

    // Aktualizacja pozycji światła
    projectileLight.position.copy(projectile.position);

    // Dodaj efekt śladu laserowego co kilka klatek
    if (Math.random() > 0.6) {
      // Częstsze cząsteczki
      createLaserTrail();
    }

    // Losowo twórz efekt promienia lasera w kierunku lotu
    if (Math.random() > 0.85) {
      const beamEnd = projectile.position.clone().add(velocity.clone().normalize().multiplyScalar(2));
      createLaserBeam(projectile.position.clone(), beamEnd);
    }

    // Pulsowanie światła dla efektu niestabilnej energii
    projectileLight.intensity = PROJECTILE_CONFIG.LASER_INTENSITY * (0.8 + Math.sin(time * 25) * 0.2);

    // Pulsowanie rozmiaru pocisku dla efektu niestabilnej energii
    const pulseScale = 1 + Math.sin(time * 20) * 0.1;
    projectile.scale.set(pulseScale, pulseScale, pulseScale);

    // Efekt rotacji pocisku
    projectile.rotation.x += 0.08;
    projectile.rotation.y += 0.1;

    // Check for tank collisions
    const tanks = window.gameState?.tanks ?? [];

    const collisionRadius = 3;
    const collisionRadiusSq = collisionRadius * collisionRadius;
    const hitTank = tanks.find((tankInstance) => projectile.position.distanceToSquared(tankInstance.mesh.position) < collisionRadiusSq);

    // Handle tank hit
    if (hitTank) {
      // Get the Tank instance from the mesh's userData
      const tankInstance = hitTank as Tank;

      if (tankInstance) {
        // Przekazujemy sourcePlayerId do handleProjectileHit aby poprawnie przypisać kille
        handleProjectileHit(projectile.position, tankInstance, sourcePlayerId);

        // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
        // document.dispatchEvent(new CustomEvent('projectile:hit', {
        //   detail: {
        //     position: projectile.position.clone(),
        //     direction: direction.clone(),
        //     playerId: sourcePlayerId || 'unknown'
        //   }
        // }));

        // Odtwórz dźwięk uderzenia lasera
        // if (typeof playSound === 'function') {
        //   playSound('laser_impact', 1.0);
        // }

        // Efekt promieni laserowych uderzających w cel
        for (let i = 0; i < 4; i++) {
          // Tworzymy promienie z różnych kierunków uderzające w cel
          const startOffset = new THREE.Vector3(
            (Math.random() - 0.5) * 8,
            3 + Math.random() * 4,
            (Math.random() - 0.5) * 8,
          );
          const startPos = projectile.position.clone().add(startOffset);
          createLaserBeam(startPos, projectile.position.clone());

          // Opóźnione tworzenie kolejnych promieni
          setTimeout(() => {
            if (scene) {
              // Sprawdź czy scena nadal istnieje
              const startPos2 = projectile.position
                .clone()
                .add(new THREE.Vector3((Math.random() - 0.5) * 6, 2 + Math.random() * 3, (Math.random() - 0.5) * 6));
              createLaserBeam(startPos2, projectile.position.clone());
            }
          }, i * 40);
        }

        // Create enhanced explosion effect at hit position
        const explosion = createLaserExplosion(scene, projectile.position, 1.2);
      } else {
        console.error("Tank hit but no Tank instance found in userData:", hitTank);
      }

      // Remove projectile and its light
      scene.remove(projectile);
      scene.remove(projectileLight);
      scene.remove(glowMesh);
      return;
    }

    // Sprawdzenie czy pocisk uderzył w ziemię
    if (projectile.position.y <= 0) {
      // Ustaw pozycję wybuchu na poziomie ziemi
      const explosionPosition = projectile.position.clone();
      explosionPosition.y = 0;

      // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
      // document.dispatchEvent(new CustomEvent('projectile:hit', {
      //   detail: {
      //     position: projectile.position.clone(),
      //     direction: direction.clone(),
      //     playerId: sourcePlayerId || 'unknown'
      //   }
      // }));

      // Odtwórz dźwięk uderzenia lasera
      // if (typeof playSound === 'function') {
      //   playSound('laser_impact', 1.0);
      // }

      // Efekt promieni laserowych rozchodzących się po ziemi
      const groundRadius = 6; // Mniejszy promień niż błyskawica
      const beamCount = 6; // Liczba promieni

      for (let i = 0; i < beamCount; i++) {
        const angle = (i / beamCount) * Math.PI * 2;
        const endPos = explosionPosition
          .clone()
          .add(new THREE.Vector3(Math.cos(angle) * groundRadius, 0, Math.sin(angle) * groundRadius));

        // Tworzymy promień od punktu uderzenia
        createLaserBeam(explosionPosition.clone(), endPos);

        // Dodatkowe, opóźnione promienie dla lepszego efektu
        setTimeout(() => {
          if (scene) {
            // Sprawdź czy scena nadal istnieje
            const randomAngle = Math.random() * Math.PI * 2;
            const randomRadius = 2 + Math.random() * 4;
            const randomEndPos = explosionPosition
              .clone()
              .add(new THREE.Vector3(Math.cos(randomAngle) * randomRadius, 0, Math.sin(randomAngle) * randomRadius));
            createLaserBeam(explosionPosition.clone(), randomEndPos);
          }
        }, i * 30);
      }

      // Efekt eksplozji
      createLaserExplosion(scene, explosionPosition, 1.0);

      // Usuń pocisk i jego światło
      scene.remove(projectile);
      scene.remove(projectileLight);
      scene.remove(glowMesh);
      return;
    }

    // Sprawdzenie maksymalnego czasu lotu
    if (time > PROJECTILE_CONFIG.RANGE / projectileSpeed) {
      // Usuń pocisk i jego światło bez efektu eksplozji
      scene.remove(projectile);
      scene.remove(projectileLight);
      scene.remove(glowMesh);
      return;
    }

    // Kontynuuj animację
    requestAnimationFrame(animateProjectile);
  };

  // Rozpocznij animację
  animateProjectile();
}
